import React, { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Copy, ChevronDown, Check } from "lucide-react";
import AdminClient from "../../services/dashboard/AdminClient";
import toast from "react-hot-toast";

/**
 * AdminFormModal
 *
 * Props:
 *  - admin (optional) : AdminDTO to edit
 *  - roles (optional) : array of RoleDTOs [{ id, name, description }]
 *  - onClose(result)  : called when modal closes; if result is an AdminDTO, parent can refresh
 *
 * Notes:
 *  - If roles prop is provided, a checkbox list is shown for assigning roles.
 *  - If not provided, the component falls back to the old comma input behavior.
 */

function initials(name = "") {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 0) return "A";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function AdminFormModal({ admin = null, roles = null, onClose }) {
  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    roleNames: [], // array of role names
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  useEffect(() => {
    if (admin) {
      setForm({
        name: admin.name || "",
        username: admin.username || "",
        email: admin.email || "",
        password: "",
        roleNames: (admin.roles || []).map((r) => r.name),
      });
    }
  }, [admin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (isRoleDropdownOpen && !event.target.closest('.role-dropdown')) {
        setIsRoleDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isRoleDropdownOpen]);

  const roleLookup = useMemo(() => {
    if (!roles) return {};
    const map = {};
    roles.forEach((r) => {
      map[r.name] = r;
    });
    return map;
  }, [roles]);

  function setField(name, value) {
    setForm((p) => ({ ...p, [name]: value }));
  }

  function toggleRole(roleName) {
    setForm((p) => {
      const set = new Set(p.roleNames || []);
      if (set.has(roleName)) set.delete(roleName);
      else set.add(roleName);
      return { ...p, roleNames: Array.from(set) };
    });
  }

  function validate() {
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Name is required (min 2 chars)");
      return false;
    }
    if (!form.username || form.username.trim().length < 3) {
      toast.error("Username is required");
      return false;
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Valid email is required");
      return false;
    }
    // For create require password, for update password optional
    if (!admin && (!form.password || form.password.length < 6)) {
      toast.error("Password required (min 6 chars)");
      return false;
    }
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const payload = {
      name: form.name,
      username: form.username,
      email: form.email,
      // only send password if provided
      ...(form.password ? { password: form.password } : {}),
      roleNames: form.roleNames && form.roleNames.length ? form.roleNames : undefined,
    };

    try {
      let res;
      if (admin) {
        res = await AdminClient.updateAdmin(admin.adminId, payload);
      } else {
        res = await AdminClient.createAdmin(payload);
      }
      const data = res.data?.responseData ?? res.data ?? res;
      toast.success(admin ? "Admin updated" : "Admin created");
      // return the saved admin DTO to caller (useful to update state)
      onClose && onClose(data);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.responseMessage || err.message || "Error saving admin";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    onClose && onClose(null);
  }

  function copyUsername() {
    navigator.clipboard?.writeText(form.username || "").then(() => toast.success("Username copied"));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-2xl shadow-xl p-6 text-gray-900 font-nunito">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 font-bold text-lg">
              {initials(form.name || admin?.name)}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{admin ? "Edit Admin" : "Create Admin"}</h3>
              <p className="text-xs text-gray-600">{admin ? `Editing ${admin.username}` : "Create a new admin user"}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => { /* keep tiny quick close */ onClose && onClose(null); }}
              className="px-3 py-1 rounded-md text-sm bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-700 font-medium">Full name</label>
            <input
              name="name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Inda Manager"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs text-gray-700 font-medium">Username</label>
            <div className="mt-1 flex gap-2">
              <input
                name="username"
                value={form.username}
                onChange={(e) => setField("username", e.target.value)}
                disabled={!!admin}
                className="flex-1 px-3 py-2 rounded-lg bg-gray-50 border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="unique username"
              />
              <button type="button" onClick={copyUsername} title="Copy username" className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600">
                <Copy size={14} />
              </button>
            </div>
            {admin && <div className="text-xs text-gray-500 mt-1">Username is immutable</div>}
          </div>

          <div className="md:col-span-1">
            <label className="text-xs text-gray-700 font-medium">Email</label>
            <input
              name="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="text-xs text-gray-700 font-medium">Password</label>
            <div className="mt-1 relative">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-300 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                placeholder={admin ? "Leave blank to keep current password" : "Enter a secure password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {admin ? "Leave blank to keep current password" : "Minimum 6 characters recommended"}
            </div>
          </div>

          {/* Roles area */}
          <div className="md:col-span-2">
            <label className="text-xs text-gray-700 dark:text-gray-300 font-medium mb-2 inline-block">Roles</label>

            {Array.isArray(roles) ? (
              <div className="mt-2 relative role-dropdown">
                {/* Dropdown Button */}
                <button
                  type="button"
                  onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                  className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-left text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-500 flex items-center justify-between"
                >
                  <span className="flex-1">
                    {form.roleNames.length === 0 
                      ? "Select roles..." 
                      : form.roleNames.length === 1 
                        ? form.roleNames[0]
                        : `${form.roleNames.length} roles selected`
                    }
                  </span>
                  <ChevronDown 
                    size={16} 
                    className={`text-gray-400 transition-transform duration-200 ${isRoleDropdownOpen ? 'rotate-180' : ''}`} 
                  />
                </button>

                {/* Dropdown Menu */}
                {isRoleDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {roles.map((r) => {
                      const isSelected = form.roleNames.includes(r.name);
                      return (
                        <button
                          key={r.name}
                          type="button"
                          onClick={() => toggleRole(r.name)}
                          className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center justify-between ${
                            isSelected 
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                              : 'text-gray-900 dark:text-white'
                          }`}
                        >
                          <span>{r.name}</span>
                          {isSelected && (
                            <Check size={16} className="text-blue-600 dark:text-blue-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected roles display */}
                {form.roleNames.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.roleNames.map((roleName) => (
                      <span
                        key={roleName}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                      >
                        {roleName}
                        <button
                          type="button"
                          onClick={() => toggleRole(roleName)}
                          className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-2">
                <input
                  name="roleNames"
                  value={(form.roleNames || []).join(", ")}
                  onChange={(e) => setField("roleNames", e.target.value.split(",").map(x => x.trim()).filter(Boolean))}
                  className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 shadow-sm"
                  placeholder="ROLE_SUPER_ADMIN, ROLE_EDITOR"
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">Provide comma-separated role names if roles list is not available.</div>
              </div>
            )}
          </div>

          {/* actions */}
          <div className="md:col-span-2 flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-sm ${saving ? "bg-blue-300 text-blue-800" : "bg-blue-600 text-white hover:bg-blue-700"}`}
            >
              {saving ? "Saving..." : (admin ? "Update Admin" : "Create Admin")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
